<script lang="ts" setup>
import { onMounted, Ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { email, required } from '@vuelidate/validators';

import { EMIT } from '@/enum';
import { useTenantStore, useTimezoneStore } from '@/store';
import { storeToRefs } from 'pinia';

const emit = defineEmits([EMIT.SAVE, EMIT.BACK]);
const timezoneStore = useTimezoneStore();
const tenantStore = useTenantStore();

/**
 * The data ref object for newAdmin so you can come back to the data.
 */
const { newAdmin } = storeToRefs(tenantStore);

/**
 * The required validation rules.
 */
const rules = {
  first_name: { required },
  last_name: { required },
  email: { required, email },
  timezone_id: { required },
  timezone: { required },
  timezone_object: { required },
};

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, newAdmin);

onMounted(() => {
  init();
});

const init = async () => await timezoneStore.getTimezones();

/**
 * Handle the client side validation, followed by saving the
 * information for the new user account.
 */
const onSave = () => {
  if (v$.value.$invalid) return;
  emit(EMIT.SAVE);
};

const goBack = () => emit(EMIT.BACK);

const handleTimezoneChange = (zone: Ref) => {
  newAdmin.value.timezone = zone.value.name;
  newAdmin.value.timezone_id = zone.value.id;
};
</script>

<template>
  <div class="field mt-2">
    <label class="required" for="name">First Name</label>
    <InputText id="first_name" v-model="v$.first_name.$model" type="text" placeholder="First Name" class="w-full" />
    <div v-for="error of v$.first_name.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>
  <div class="field mt-2">
    <label class="required" for="name">Last Name</label>
    <InputText id="last_name" v-model="v$.last_name.$model" type="text" placeholder="Last Name" class="w-full" />
    <div v-for="error of v$.last_name.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>

  <div class="field mt-2">
    <label class="required" for="street">Email</label>
    <InputText id="email" v-model="v$.email.$model" type="email" placeholder="Email" class="w-full" />
    <div v-for="error of v$.email.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>
  <div class="field mt-2">
    <label class="required" for="timezone">Timezone</label>
    <Dropdown
      id="timezone"
      v-model="v$.timezone_object.$model"
      :options="timezoneStore.timezones"
      option-label="name"
      placeholder="Select a Timezone"
      class="w-full"
      @change="handleTimezoneChange"
    />
    <div v-for="error of v$.timezone.$errors" :key="error.$uid" class="input-errors">
      <small>{{ error.$message }}</small>
    </div>
  </div>

  <div class="dialog-footer flex justify-content-between">
    <AppButton icon-left="arrow_back" type="secondary" text="Back" class="ml-0 admin-button" @click="goBack" />
    <AppButton
      icon-left="arrow_forward"
      type="secondary"
      text="Create Tenant"
      class="admin-button cursor-pointer"
      :disabled="v$.$invalid"
      @click="onSave"
    />
  </div>
</template>

<style lang="scss" scoped>
.input-errors {
  color: $errorColor;
}

:deep(.p-card-body) {
  padding: 0;

  .p-card-content {
    padding: 26px;
  }
}

:deep(.p-card-footer) {
  padding: 24px;
  margin: 0;
  background: white;
  align-items: center;
}
</style>
